import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { shareCurrentUrl, isShareSupported as shouldShowShare } from 'src/lib/shareUtil'

interface SheetHeaderProps {
    children?: React.ReactNode
    heading: React.ReactNode
    menuItems?: React.ReactNode[]
    share?: boolean
    actions?: React.ReactNode
}

const SheetHeader = ({ children, heading, menuItems, share, actions }: SheetHeaderProps) => {
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const menu = makeMenu(menuItems || [], share || true, shouldShowShare(), t, open, anchorEl, handleClick, handleClose)
    const hasRightContent = actions || menu.length > 0

    return (
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {heading}
                </Typography>
                {hasRightContent && (
                    <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0} ml={1}>
                        {actions}
                        {...menu}
                    </Stack>
                )}
            </Stack>
            {children}
        </Box>
    )
}

function makeMenu(menuItems: React.ReactNode[], share: boolean, shouldShow: boolean, t: (key: string) => string, open: boolean, anchorEl: HTMLElement | null, handleClick: (event: React.MouseEvent<HTMLElement>) => void, handleClose: () => void) {
    if (share === undefined) share = true

    if (shouldShow && share)
        menuItems = [(
            <MenuItem key="share" onClick={shareCurrentUrl}>
                {t('Share link')}
            </MenuItem>
        ), ...menuItems]

    if (menuItems.length == 0)
        return []

    return [
        (
            <IconButton
                key="more"
                aria-label={t('more')}
                id="long-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                size="small"
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
        ),
        (
            <Menu
                key="menu"
                id="long-menu"
                MenuListProps={{
                    'aria-labelledby': 'long-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {...menuItems}
            </Menu>
        )]
}

export default SheetHeader
